import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { TaskStatusBadge, PriorityBadge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Calendar, Plus, Filter, ChevronDown, Eye, MessageSquare } from 'lucide-react';
import type { Task } from '@/types';
import { formatDate } from '@/lib/utils';

interface ActiveWorkOverviewProps {
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
  onNewRequest?: () => void;
}

export function ActiveWorkOverview({ tasks, onTaskClick, onNewRequest }: ActiveWorkOverviewProps) {
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredTasks = tasks.filter((task) => {
    if (filter === 'active') return task.status !== 'approved' && task.status !== 'delivered';
    if (filter === 'completed') return task.status === 'approved' || task.status === 'delivered';
    return true;
  }).filter((task) => {
    if (statusFilter === 'all') return true;
    return task.status === statusFilter;
  });

  const statusCounts = {
    queued: tasks.filter(t => t.status === 'queued').length,
    'in-progress': tasks.filter(t => t.status === 'in-progress').length,
    review: tasks.filter(t => t.status === 'review').length,
    delivered: tasks.filter(t => t.status === 'delivered').length,
    approved: tasks.filter(t => t.status === 'approved').length,
  };

  return (
    <Card
      title="Active Work Overview"
      subtitle={`${tasks.filter(t => t.status !== 'approved').length} active tasks • ${tasks.length} total`}
      action={
        <Button onClick={onNewRequest} leftIcon={<Plus className="w-4 h-4" />} size="sm">
          New Request
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Status summary */}
        <div className="grid grid-cols-5 gap-3">
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
              className={`p-3 rounded-lg border transition-all ${
                statusFilter === status
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-slate-50 border-slate-100 hover:bg-slate-100'
              }`}
            >
              <div className="text-2xl font-bold text-slate-900">{count}</div>
              <div className="text-xs text-slate-500 capitalize">{status.replace('-', ' ')}</div>
            </button>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
          <div className="flex gap-2">
            {(['all', 'active', 'completed'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  filter === f
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <Filter className="w-4 h-4" />
            Filter
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        {/* Tasks list */}
        <div className="space-y-3">
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>No tasks found</p>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <div
                key={task.id}
                onClick={() => onTaskClick?.(task)}
                className="group p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer bg-white"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <TaskStatusBadge status={task.status} />
                      <PriorityBadge priority={task.priority} />
                      <span className="text-xs text-slate-400">#{task.id}</span>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                      {task.title}
                    </h4>
                    <p className="text-sm text-slate-500 line-clamp-2">{task.description}</p>
                    
                    <div className="flex items-center gap-4 mt-3">
                      {task.assignedDeveloper && (
                        <div className="flex items-center gap-2">
                          <Avatar
                            src={task.assignedDeveloper.avatar}
                            name={task.assignedDeveloper.name}
                            size="xs"
                          />
                          <span className="text-sm text-slate-600">{task.assignedDeveloper.name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Calendar className="w-4 h-4" />
                        <span>Due {formatDate(task.estimatedCompletion)}</span>
                      </div>
                      {task.comments.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <MessageSquare className="w-4 h-4" />
                          <span>{task.comments.length}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Eye className="w-4 h-4" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTaskClick?.(task);
                      }}
                    >
                      View
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
}
